import useLogin from "@common/components/hooks/useLogin";
import React from "react";
import { Controller } from "react-hook-form";
import { Form, Label } from "semantic-ui-react";

/**
 * SelectComponent renders a select field with validation.
 *
 * @typedef {object} SelectProps
 * @property {string} name - Name of the select field.
 * @property {string} [label=''] - Label of the select field.
 * @property {string} placeholder - Placeholder text of the select field.
 * @property {Function} control - Function to control the select field for form
 * @property {Object} [error=null] - Object containing validation error.
 * @property {array} options - Options for the select component.
 * @property {boolean} [loading=false] - Loading flag for the select component.
 * @property {string} [labelStyle='w-30'] - Style for the label.
 * @property {string} [fieldStyle='flex-between flex-row-center'] - Style for Form.Field.
 * @property {string} [selectContainerStyle='w-100'] - Style for select container.
 * @property {number} [selectWidth=16] - Width of the Form.Select component.
 *
 * @component
 * @param {SelectProps} props - The component props.
 * @returns {JSX.Element} The rendered select form component with validation.
 */
export default function SelectComponent({
  name,
  label = "",
  placeholder,
  control,
  error = null,
  options,
  loading = false,
  labelStyle = "w-30",
  fieldStyle = "flex-between flex-row-center",
  selectContainerStyle = "w-100",
  selectWidth = 16,
}) {
  const [{ isInReadMode }] = useLogin();
  return (
    <Form.Field className={fieldStyle} error={!!error}>
      {label && <label className={labelStyle}>{label}</label>}
      <div className={selectContainerStyle}>
        <Controller
          name={name}
          control={control}
          render={({ field: { onChange, value } }) => {
            if (isInReadMode) {
              const readValue =
                value?.name ??
                options.find((x) => {
                  return (
                    (x.value && x.value === value) ||
                    (x.id && x.id === value) ||
                    (x.value && value?.value && x.value === value.value) ||
                    (x.id && value.id && x.id === value.id)
                  );
                })?.text;

              return <Form.Input value={readValue} readOnly />;
            }

            return (
              <Form.Select
                placeholder={placeholder}
                options={options}
                value={value}
                loading={loading}
                onChange={(_, { value }) => {
                  onChange(value);
                }}
                width={selectWidth}
              />
            );
          }}
        />
        {error && (
          <Label pointing="above" prompt className="m-0">
            {error?.message}
          </Label>
        )}
      </div>
    </Form.Field>
  );
}
